import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const POLL_INTERVAL_MS = 30000;

// Convert URL-base64 VAPID key to Uint8Array for PushManager.subscribe
const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};

const NotificationsContext = createContext({
  unreadCount: 0,
  notifications: [],
  refresh: () => {},
  markRead: () => {},
  markAllRead: () => {},
  remove: () => {},
  pushEnabled: false,
  enableBrowserPush: () => {},
});

export const NotificationsProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [browserPushEnabled, setBrowserPushEnabled] = useState(
    typeof Notification !== "undefined" && Notification.permission === "granted"
  );
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const lastSeenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get("/notifications?limit=50");
      const items = res.data.notifications || [];
      setUnreadCount(res.data.unread_count || 0);

      // Detect new (unseen) notifications and surface a toast + browser notification
      if (initializedRef.current) {
        const fresh = items.filter((n) => !lastSeenIdsRef.current.has(n.id) && !n.is_read);
        for (const n of fresh) {
          toast(n.message, {
            description: `Triggered ${new Date(n.created_at).toLocaleTimeString()}`,
            action: {
              label: "View",
              onClick: () => {
                window.location.href = `/asset/${n.asset_type}/${n.symbol}`;
              },
            },
          });
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try {
              new Notification("UpTick alert", {
                body: n.message,
                icon: "/favicon.ico",
                tag: n.id,
              });
            } catch (e) {
              // Browser-side Notification API can fail when tab is unfocused
              // or permission silently revokes. Warn but don't break the toast path.
              console.warn("Notification API call failed:", e);
            }
          }
        }
      }

      lastSeenIdsRef.current = new Set(items.map((n) => n.id));
      initializedRef.current = true;
      setNotifications(items);
    } catch (e) {
      // Poller continues on next tick; surface only in dev console.
      console.warn("Notifications poll failed:", e);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      lastSeenIdsRef.current = new Set();
      initializedRef.current = false;
      return undefined;
    }
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user, refresh]);

  const markRead = useCallback(async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) {
      console.warn("markRead failed:", e);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.warn("markAllRead failed:", e);
    }
  }, []);

  const remove = useCallback(async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (e) {
      console.warn("notification delete failed:", e);
    }
  }, []);

  const enableBrowserPush = useCallback(async () => {
    if (typeof Notification === "undefined") {
      toast.error("Browser notifications are not supported here");
      return false;
    }
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      toast.error("Push notifications aren't supported in this browser");
      return false;
    }
    if (Notification.permission === "denied") {
      toast.error("Notifications are blocked. Enable them in your browser settings.");
      return false;
    }

    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permission denied");
        return false;
      }
      setBrowserPushEnabled(true);

      // Register service worker (idempotent — browser dedupes by URL)
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      // Fetch VAPID public key from backend
      const keyRes = await api.get("/push/vapid-public-key");
      if (!keyRes.data?.key || !keyRes.data?.configured) {
        toast.error("Push notifications aren't configured on the server");
        return false;
      }

      // Subscribe (or reuse existing subscription)
      const existing = await reg.pushManager.getSubscription();
      const subscription =
        existing ||
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(keyRes.data.key),
        }));

      // Tell the backend
      const sub = subscription.toJSON();
      await api.post("/push/subscribe", {
        endpoint: sub.endpoint,
        keys: sub.keys,
      });
      setPushSubscribed(true);
      toast.success("Push notifications enabled — even when this tab is closed");
      return true;
    } catch (e) {
      console.error("push enable failed", e);
      toast.error("Could not enable push notifications");
      return false;
    }
  }, []);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        refresh,
        markRead,
        markAllRead,
        remove,
        browserPushEnabled,
        pushSubscribed,
        enableBrowserPush,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationsContext);
